import classNames from "classnames";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { CSSTransition } from "react-transition-group";
import { getMobileCenteredShellPortalRoot } from "@/layout/components/mobileCenteredShell/portal";
import Portal from "@/componentsUI/Portal";

interface PopupMaskProps {
  children?: React.ReactNode;
  visible?: boolean;
  className?: string;
  onClose?: () => void;
  dialogLabelledBy?: string;
  dialogDescribedBy?: string;
}

export interface PopupMaskRef {
  getWrapperRef: () => HTMLDivElement | null;
}

const PopupMask = forwardRef<PopupMaskRef, PopupMaskProps>((props, ref) => {
  const {
    children,
    visible = false,
    className,
    onClose,
    dialogLabelledBy,
    dialogDescribedBy,
  } = props;

  const prefixCls = "jokerma-popup";
  const wrapperRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    () => getMobileCenteredShellPortalRoot()
  );

  useImperativeHandle(ref, () => ({
    getWrapperRef: () => wrapperRef.current,
  }));

  const handleMaskClick = () => {
    onClose?.();
  };

  const handleWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  useEffect(() => {
    const syncPortalContainer = () => {
      setPortalContainer(getMobileCenteredShellPortalRoot());
    };
    const observer = new MutationObserver(syncPortalContainer);

    syncPortalContainer();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Portal
      key={portalContainer?.id || "body"}
      visible={visible}
      forceRender
      getContainer={() => portalContainer || document.body}
    >
      <div className={classNames(`${prefixCls}-container`, className)}>
        <CSSTransition
          in={visible}
          timeout={400}
          appear
          mountOnEnter={false}
          classNames="fadeModal"
          unmountOnExit={false}
          nodeRef={maskRef}
          onEnter={() => {
            if (maskRef.current) {
              maskRef.current.style.display = "block";
            }
          }}
          onExited={() => {
            if (maskRef.current) {
              maskRef.current.style.display = "none";
            }
          }}
        >
          <div
            ref={maskRef}
            aria-hidden="true"
            className={`${prefixCls}-mask`}
            onClick={handleMaskClick}
          />
        </CSSTransition>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogLabelledBy}
          aria-describedby={dialogDescribedBy}
          ref={wrapperRef}
          onClick={handleWrapperClick}
          className={classNames(
            `${prefixCls}-wrapper`,
            `${prefixCls}-wrapper-align-center`,
            {
              [`${prefixCls}-wrapper-hidden`]: !visible,
            }
          )}
        >
          <div
            className={classNames(`${prefixCls}`, `${prefixCls}-content`)}
            onClick={handleWrapperClick}
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
});

PopupMask.displayName = "PopupMask";

export default PopupMask;
